import {
  commitPendingDesktopViewTransform,
  fitDesktopView,
  resetDesktopViewTouchState,
} from "./desktop-preview-touch.js";
import { isMobileShellVisible } from "../../../../shared/utils/device-detect.js";

let getCallbacks = () => null;
let getContainer = () => null;
let uiState = null;
let mountedDesktopViewName = null;
// Key of the mounted view+doc, and per-key stashes of the user's live
// pan/zoom captured at unmount (destroy() resets the view state) so a tab
// revisit restores their framing instead of re-fitting.
let mountedFitKey = null;
const savedPreviewTransforms = new Map();
let originalDesktopViewParents = new Map();

export function configureDesktopPreviewMount(config) {
  getCallbacks = config.getCallbacks;
  getContainer = config.getContainer;
  uiState = config.uiState;
}

export function getMountedDesktopViewName() {
  return mountedDesktopViewName;
}

export function syncDesktopPreviewMount(appState) {
  const container = getContainer();
  // Mindmap and Bubbles are their own tabs now, so the tab name *is* the view
  // to mount. (Previously both lived under a "more" tab with a sub-toggle.)
  // The shell-visibility check releases the desktop view element back to #app
  // when the viewport flips to desktop width — otherwise it would stay
  // captive inside the hidden mobile shell and the desktop canvas would be
  // empty whenever currentView is mindmap/bubble.
  const desiredView =
    (uiState?.tab === "mindmap" || uiState?.tab === "bubble") &&
    appState?.data &&
    isMobileShellVisible()
      ? uiState.tab
      : null;

  // If a different desktop view is mounted, unmount it first.
  if (mountedDesktopViewName && mountedDesktopViewName !== desiredView) {
    unmountDesktopView(mountedDesktopViewName);
  }

  if (!desiredView) return;

  const host = container?.querySelector(`[data-mobile-desktop-view-host="${desiredView}"]`);
  if (!host) return;

  mountDesktopView(desiredView, host);
}

export function fitMountedDesktopView() {
  if (!mountedDesktopViewName) return;
  fitDesktopView(mountedDesktopViewName);
}

export function detachMountedDesktopViewToOriginalParent() {
  if (!mountedDesktopViewName) return;
  const desktopEl = document.getElementById(`${mountedDesktopViewName}-view`);
  if (!desktopEl) {
    mountedDesktopViewName = null;
    return;
  }
  restoreDesktopElToOriginalParent(mountedDesktopViewName, desktopEl);
  // Keep mountedDesktopViewName so syncDesktopPreviewMount knows whether the
  // user is still asking for this view after the mobile shell re-renders.
}

export function destroyDesktopPreviewMount() {
  if (mountedDesktopViewName) {
    unmountDesktopView(mountedDesktopViewName);
  }
  originalDesktopViewParents.clear();
}

function mountDesktopView(viewName, host) {
  const callbacks = getCallbacks();
  const desktopEl = document.getElementById(`${viewName}-view`);
  if (!desktopEl) return;
  if (!originalDesktopViewParents.has(viewName)) {
    originalDesktopViewParents.set(viewName, {
      parent: desktopEl.parentElement,
      next: desktopEl.nextSibling,
      classes: desktopEl.className,
    });
  }
  const wasMounted = desktopEl.parentElement === host;
  if (!wasMounted) {
    host.appendChild(desktopEl);
  }
  desktopEl.classList.add("active", "mobile-embedded-view");
  mountedDesktopViewName = viewName;
  const docId = callbacks?.sharedCallbacks?.getCurrentDocId?.()
    || callbacks?.getCurrentDocId?.() || "";
  const newKey = `${viewName}:${docId}`;
  // Doc or view changed since the last mount of this module → the old
  // framing is meaningless, force a stash-restore or fit below even if a
  // live preview transform survived.
  const keyChanged = newKey !== mountedFitKey;
  mountedFitKey = newKey;
  // Trigger render so the view fills the new container size, then settle the
  // framing. Priority: (1) a live preview transform that survived (shell
  // remounts don't destroy the view) — leave it alone; (2) a stash captured
  // at the last unmount of this view+doc — restore the user's framing;
  // (3) nothing known — auto-fit. The old code re-fit on every tab revisit,
  // throwing the user's pan/zoom away.
  if (callbacks?.renderDesktopView) {
    requestAnimationFrame(() => {
      callbacks.renderDesktopView(viewName);
      requestAnimationFrame(() => {
        if (getMountedDesktopViewName() !== viewName) return;
        const live = callbacks.getDesktopViewTransform?.(viewName);
        if (!keyChanged && live?._mobilePreview) return;
        const stash = savedPreviewTransforms.get(newKey);
        if (stash) {
          callbacks.setDesktopViewTransform?.(viewName, stash, { commitToStore: false });
        } else {
          fitDesktopView(viewName);
        }
      });
    });
  }
}

function unmountDesktopView(viewName) {
  const callbacks = getCallbacks();
  // Apply any pending mobile pinch/pan before tearing down so the embedded
  // view is internally consistent during cleanup.
  commitPendingDesktopViewTransform();
  resetDesktopViewTouchState();
  // Stash the live framing before destroy() wipes it — the next mount of
  // this view+doc restores it instead of auto-fitting.
  if (mountedFitKey && viewName === mountedDesktopViewName) {
    const live = callbacks?.getDesktopViewTransform?.(viewName);
    if (live) {
      savedPreviewTransforms.set(mountedFitKey, {
        panX: live.panX, panY: live.panY, zoom: live.zoom,
      });
    }
  }
  const desktopEl = document.getElementById(`${viewName}-view`);
  if (!desktopEl) {
    mountedDesktopViewName = null;
    return;
  }
  callbacks?.destroyDesktopView?.(viewName);
  restoreDesktopElToOriginalParent(viewName, desktopEl);
  mountedDesktopViewName = null;
}

function restoreDesktopElToOriginalParent(viewName, desktopEl) {
  const original = originalDesktopViewParents.get(viewName);
  if (original?.parent && desktopEl.parentElement !== original.parent) {
    if (original.next && original.next.parentElement === original.parent) {
      original.parent.insertBefore(desktopEl, original.next);
    } else {
      original.parent.appendChild(desktopEl);
    }
  }
  desktopEl.classList.remove("mobile-embedded-view");
  if (original?.classes !== undefined) {
    desktopEl.className = original.classes;
  }
}
